import type { ManagedImageData, ObservedImage } from '../media/media-model';
// The Data tab's inputs (DES1 `app-parcels-data`). Headless: APP/DATA supply tables, rows and
// history; this only shapes them for display. A missing value is null and renders as a dash, never
// zero; a denied or unauthorised row is simply not in `rows`.
import type { ConnectionMode } from '../publish/source-modes-model';

export type DataCell = string | number | boolean | null;

export type DataTable = {
  id: string;
  name: string;
  kind: 'table' | 'files';
  /** Absent while the count is not known (never a guess); a files table always has one. */
  entries?: number;
  fields?: number;
  lastChange?: { at: string; by: string } | null;
  /** For a files table: a ready size string ("412 MB"). */
  size?: string;
};

export type DataColumn = {
  key: string;
  label: string;
  numeric?: boolean;
  /** Shown as a pill (a status or severity). */
  pill?: boolean;
  /** One of the first columns that leads each stacked row on a phone (the key). */
  lead?: boolean;
  /** Holds a governed image: drawn by APP's shared image primitive as a 40 px thumbnail. */
  image?: boolean;
  /** The column's declared type, when the read carries one (the native Data read does): the readable view uses it. */
  type?: 'text' | 'integer' | 'decimal' | 'boolean' | 'date' | 'timestamp' | 'json' | 'file';
};

export type DataRow = {
  id: string;
  cells: Record<string, DataCell>;
  /** ART's image VM data by image column key; APP's shared primitive draws them. */
  images?: Record<string, ManagedImageData>;
};

export type RowsState =
  | { status: 'loading' }
  | { status: 'failed' }
  | {
      status: 'ready';
      columns: DataColumn[];
      rows: DataRow[];
      hasMore: boolean;
      /** Images captured vs. expected, when the capture is partial. */
      capture?: { shown: number; total: number };
    };

export type RowChange =
  | { kind: 'created'; at: string; by: string; undoToken?: string }
  | {
      kind: 'changed';
      at: string;
      by: string;
      field: string;
      from: DataCell;
      to: DataCell;
      undoToken?: string;
      /** The image as observed at this version, when the changed field is an image. */
      image?: ObservedImage;
    };

export type RowHistoryState =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; changes: RowChange[] };

export type DataSource = {
  id: string;
  name: string;
  system: string;
  access: 'read' | 'read_write';
  allowedBy: string;
  allowedAt: string;
  lastReadAt: string | null;
  /** The published binding's mode (never live policy). */
  mode: ConnectionMode;
  ownerName: string;
};

export type KeptHistory = { id: string; name: string; since: string };

/** A removal waiting for an editor. The effect is worded from these counts, never free text. */
export type PendingRemoval = {
  id: string;
  field: string;
  entries: number;
  withValue: number;
  requestedBy: string;
  /** Who started it, as an origin label ("Poneva's builder", "via <agent>"). */
  origin: string;
};

/** A value for display: absent is a dash, a boolean is spelled by the caller's locale-free word. */
export function displayCell(value: DataCell, absent: string): string {
  if (value === null || value === undefined) return absent;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : absent;
  return String(value);
}

/** Undo is offered only when the change carries a token the server can still honour. */
export const canUndo = (change: RowChange) => Boolean(change.undoToken);
